import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChefHat, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, register } = useAuth();
  const { brand } = useBrand();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      toast.success("Accesso effettuato");
      navigate("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:block lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1622021142947-da7dedc7c39a?auto=format&fit=crop&w=1400&q=80)",
        }}
      >
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <h2 className="font-display text-4xl font-extrabold tracking-tight max-w-md leading-tight">
            Ogni piatto, sotto controllo.
          </h2>
          <p className="mt-4 text-white/80 max-w-sm">
            Calcola il food cost, i valori nutrizionali e il prezzo di vendita del tuo menù in pochi secondi.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center overflow-hidden shrink-0">
              {brand.logo ? (
                <img src={brand.logo} alt="logo" className="w-full h-full object-contain" data-testid="login-logo" />
              ) : (
                <ChefHat className="w-6 h-6 text-primary-foreground" />
              )}
            </div>
            <span className="font-display font-extrabold text-2xl tracking-tight">{brand.business_name}</span>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight">
            {mode === "login" ? "Bentornato" : "Crea account"}
          </h1>
          <p className="text-muted-foreground mt-1 mb-8">
            {mode === "login" ? "Accedi al tuo gestionale" : "Inizia a controllare i costi"}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Nome locale</Label>
                <Input
                  id="name"
                  data-testid="register-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Trattoria da Paolo"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="mario@ristorante.it"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}

            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "login" ? "Accedi" : "Registrati"}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {mode === "login" ? "Non hai un account?" : "Hai già un account?"}{" "}
            <button
              className="text-primary font-medium hover:underline"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              data-testid="toggle-mode"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
